import React, { useState } from 'react';
import Table from 'components/shared/table';
import Skeleton from 'ui/Skeleton';
import { Icon } from 'components/ui';
import { Text, TEXT_VARIANTS } from 'components/ui/Typography';
import { withSkillsClientWrapper } from 'components/shared/Skills/SkillsClientProvider';
import { withReactQueryWrapper } from 'components/shared/ReactQuery/ReactQueryProvider';
import { REACT_QUERY_STATUS } from 'constants/config';
import { useQueryClient } from '@tanstack/react-query';
import {
  useGetReadinessSkills,
  SORT_OPTIONS,
  useUpdateReadinessSkill,
} from '../Skills/queries/readinessSkills';

const SkillTagTable = () => {
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState(SORT_OPTIONS.ASC);

  const page = offset / limit + 1;
  const { data: readinessSkillData, status: readinessSkillsStatus } =
    useGetReadinessSkills({
      limit,
      offset,
      searchTerm: '',
      sort,
    });

  const isSortingByCategory = sort.includes('category');
  const sortIconName = sort.includes('asc') ? 'triangle_down' : 'triangle_up';
  const skills = readinessSkillData?.readinessSkills?.readinessSkills ?? [];
  const totalCount = readinessSkillData?.readinessSkills?.totalCount;
  const pageCount = Math.ceil(totalCount / limit);
  const tableIsLoading = readinessSkillsStatus === REACT_QUERY_STATUS.LOADING;
  const queryClient = useQueryClient();

  const { mutate: updateMutate } = useUpdateReadinessSkill({
    id: '',
    isTag: false,
  });

  function handleUpdateTag(skillId, isTag) {
    updateMutate(
      {
        id: skillId,
        isTag,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('get-readiness-skills');
        },
      },
    );
  }

  return (
    <Table
      className="GradingStationTable Index-table--multipleHeaders mb-32 bg-white border border-gray-light"
      alternate
      noBorderTop
      roundedBottom
    >
      <Table.Header>
        <Table.Header.Cell
          className="Table-cell--large Table-cell--clickable Table-cell--sortable"
          onClick={() => {
            setSort(
              sort === SORT_OPTIONS.ASC ? SORT_OPTIONS.DESC : SORT_OPTIONS.ASC,
            );
            setOffset(0);
          }}
        >
          <div className="d-f fd-r ai-c">
            <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
              {t('skills.name_column')}
            </Text>
            {!isSortingByCategory && (
              <div className="icon ml-20">
                <Icon
                  name={sortIconName}
                  className="Icon--small c-brand-gray c-p"
                />
              </div>
            )}
          </div>
        </Table.Header.Cell>
        <Table.Header.Cell
          className="Table-cell--clickable Table-cell--sortable"
          onClick={() => {
            setSort(
              sort === SORT_OPTIONS.CATEGORY_ASC
                ? SORT_OPTIONS.CATEGORY_DESC
                : SORT_OPTIONS.CATEGORY_ASC,
            );
            setOffset(0);
          }}
        >
          <div className="d-f fd-r ai-c">
            <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
              {t('skills.category_column')}
            </Text>
            {isSortingByCategory && (
              <div className="icon ml-20">
                <Icon
                  name={sortIconName}
                  className="Icon--small c-brand-gray c-p"
                />
              </div>
            )}
          </div>
        </Table.Header.Cell>
        <Table.Header.Cell className="Table-cell--small">
          <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
            {t('tags.table.description')}
          </Text>
        </Table.Header.Cell>
        <Table.Header.Cell>
          <Text variant={TEXT_VARIANTS.BODY_LIGHT}>
            {t('tags.skill_tag_column')}
          </Text>
        </Table.Header.Cell>
      </Table.Header>
      {tableIsLoading
        ? [...Array(10)].map((e, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Table.Row key={`loading-row-${i}`}>
              <Table.BodyCell className="Table-cell--large">
                <Skeleton width={100 * Math.random() + 250} />
              </Table.BodyCell>
              <Table.BodyCell>
                {Math.random() > 0.2 && <Skeleton />}
              </Table.BodyCell>
              <Table.BodyCell className="Table-cell--small">
                <Skeleton width={30} />
              </Table.BodyCell>
              <Table.BodyCell>
                <Skeleton width={30} />
              </Table.BodyCell>
            </Table.Row>
          ))
        : skills?.map(sk => (
            <Table.Row className="Table-cell--clickable" key={sk?.id}>
              <Table.BodyCell className="Table-cell--large">
                {sk.name}
              </Table.BodyCell>
              <Table.BodyCell>{sk.readinessSkillCategory?.name}</Table.BodyCell>
              <Table.BodyCell className="Table-cell--small format_spaces">
                {sk.description}
              </Table.BodyCell>
              <Table.BodyCell>
                <input
                  type="checkbox"
                  key={sk?.id}
                  checked={sk.isTag}
                  onChange={() => {
                    handleUpdateTag(sk.id, !sk.isTag);
                  }}
                />
              </Table.BodyCell>
            </Table.Row>
          ))}
      <Table.Footer className="p-12 border border-gray-light">
        <Table.Pagination
          onPageChange={e => setOffset((e - 1) * 10)}
          page={page}
          pageCount={pageCount}
          perPage={limit}
          onPerPageChange={e => {
            setLimit(e);
            setOffset(0);
          }}
        />
      </Table.Footer>
    </Table>
  );
};

export default withSkillsClientWrapper(withReactQueryWrapper(SkillTagTable));
